import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../core/services/api.service';

export interface ChatProductLinesResponse {
  status?: string;
  product_lines?: string[];
  description?: Record<string, string>;
}

export interface ChatCitation {
  reference_number?: number;
  document?: string;
  download_url?: string;
  downloadUrl?: string;
  url?: string;
  file_url?: string;
  page?: string;
  page_range?: string;
  section?: string;
  relevance_score?: number;
  content_preview?: string;
  chunk_id?: string;
}

export interface ChatQueryRequest {
  query: string;
  product_line: string;
  user_id: string;
  include_images: boolean;
  session_id: string;
}

export interface ChatQueryResponse {
  question: string;
  answer: string;
  citations?: ChatCitation[];
  images?: unknown[];
  tables?: unknown[];
  metadata?: Record<string, unknown>;
  chat_message_id?: string;
}

export interface ChatSessionSummary {
  session_id: string;
  last_message_preview: string;
  last_timestamp: string;
}

export interface ChatMessageHistoryItem {
  id: string;
  user_id: string;
  session_id: string;
  msg_id?: string | null;
  role: 'user' | 'assistant' | string;
  product_line?: string | null;
  message: string;
  timestamp?: string | null;
  status?: string | null;
}

export interface ChatListResponse<T> {
  value: T[];
  Count?: number;
}

export interface ChatFeedbackRequest {
  chat_message_id: string;
  user_id: string;
  session_id: string;
  feedback_type: string;
  question: string;
  answer: string;
  correction_text?: string | null;
  reason?: string | null;
}

export interface ChatCitationDownloadResponse {
  status?: string;
  source?: string;
  download_url?: string;
  downloadUrl?: string;
  url?: string;
  file_url?: string;
  timestamp?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  constructor(private apiService: ApiService) {}

  getProductLines(): Observable<ChatProductLinesResponse> {
    return this.apiService.getWithoutSpinner('Chat/product-lines');
  }

  query(request: ChatQueryRequest): Observable<ChatQueryResponse> {
    return this.apiService.postWithoutSpinner('Chat/query', request);
  }

  getSessions(userId: string): Observable<ChatListResponse<ChatSessionSummary>> {
    const params = new URLSearchParams({ user_id: userId });
    return this.apiService.getWithoutSpinner(`Chat/sessions?${params.toString()}`);
  }

  getSessionMessages(sessionId: string, userId: string): Observable<ChatListResponse<ChatMessageHistoryItem>> {
    const params = new URLSearchParams({ user_id: userId });
    return this.apiService.getWithoutSpinner(`Chat/sessions/${encodeURIComponent(sessionId)}/messages?${params.toString()}`);
  }

  submitFeedback(request: ChatFeedbackRequest): Observable<unknown> {
    return this.apiService.postWithoutSpinner('Chat/feedback', request);
  }

  getCitationDownload(source: string): Observable<ChatCitationDownloadResponse> {
    return this.apiService.postWithoutSpinner('Chat/citation', { source });
  }
}
